import React from 'react';
import Button from 'components/global/Buttons/Button';
import { useAssessmentContext } from 'services/context/assessmentContext/useAssessmentContext';
import { FlexContainer } from 'components/local/styles/StyledComponents';
import {
  NavWrapper,
  QuestionCount
} from 'components/local/navbar/styles/StyledComponents';
import { getSubmitButtonText } from 'containers/app/helpers/getSubmitButtonText';
import { shouldSubmitAssessment } from 'containers/app/helpers/shouldSubmitAssessment';
import { QuestionTypes } from 'containers/app/helpers/questionTypes';
import { useTranslation } from 'react-i18next';

const ClickableBottomNavBar = ({ formikProps, setSlot, setModal }) => {
  const { t } = useTranslation();
  const { state, currentSection, currentSlot } = useAssessmentContext();
  const {
    currentSlotIndex,
    currentSlotId,
    slotContentIsLoading,
    assessmentAttempt,
    files
  } = state;
  const {
    assessment: { sections: totalSections }
  } = assessmentAttempt;
  const slotNumber = currentSlotIndex + 1;
  const sectionLength = currentSection.slot_attempts.length;

  const handleMoveSlot = async direction => {
    // Record the navigation target BEFORE submitting. Formik invokes onSubmit
    // before submitForm() resolves, and onSubmit is what reads this target, so
    // setting it afterwards left it holding the value from the pre-click
    // render. On the first click of a section that is the `null` seed of
    // SectionClickable's newSlot, which crashed moveToNextSlot and surfaced to
    // the taker as a fabricated 500 error page (UB-4212).
    // This setSlot is flushed when the handler suspends at the await, so
    // onSubmit sees it. Do not move it back below the await.
    setSlot(currentSlotIndex + direction);
    try {
      await formikProps.submitForm();
    } catch (error) {
      console.error('Error submitting slot before navigation:', error);
    }
  };

  const handleFinishSection = () => {
    setSlot(null);
    setModal(true);
  };

  const isAssessmentReadyToSubmit = shouldSubmitAssessment(
    totalSections.length,
    currentSection.sort_index,
    sectionLength,
    currentSlotIndex
  );
  const isVideoQuestion =
    currentSlot?.question?.question_type === QuestionTypes.UPLOAD &&
    currentSlot?.question?.accept_types?.includes('video');
  const isFileQuestion =
    currentSlot?.question?.question_type === QuestionTypes.UPLOAD &&
    currentSlot?.question?.accept_types === null;

  return (
    <NavWrapper>
      <FlexContainer
        width="100%"
        justifyContent={currentSlotIndex > 0 ? 'space-between' : 'flex-end'}
        maxWidth="1024px"
      >
        {currentSlotIndex > 0 && (
          <Button
            className="secondary nav"
            text={t('buttons.previous')}
            disabled={slotContentIsLoading}
            onClick={() => handleMoveSlot(-1)}
          ></Button>
        )}
        <QuestionCount>
          {t('questionProgress.ofTotal', {
            current: slotNumber,
            total: sectionLength
          })}
        </QuestionCount>
        <Button
          disabled={
            slotContentIsLoading ||
            !!formikProps.errors[currentSlotId] ||
            // For video questions, the object key is the id
            (isVideoQuestion && !files[currentSlot?.id]) ||
            // For file questions, the object key is the file name
            (isFileQuestion && Object.keys(files).length === 0)
          }
          className="nav"
          text={
            slotNumber === sectionLength
              ? getSubmitButtonText(!isAssessmentReadyToSubmit, true)
              : t('buttons.next')
          }
          onClick={
            slotNumber === sectionLength
              ? handleFinishSection
              : () => handleMoveSlot(1)
          }
        ></Button>
      </FlexContainer>
    </NavWrapper>
  );
};

export default ClickableBottomNavBar;
